import type {MatchMode} from '@wandb/weave/common/util/columnMatching';
import React, {useMemo, useState} from 'react';

import {useProjectFieldsQuery} from '../../../state/graphql/projectFieldsQuery';
import {useDebounceState} from '../../../util/hooks';
import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import {useDefaultKeys} from '../hooks/useDefaultKeys';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';
import {TableEditor} from './TableEditor';

const EMPTY_ARRAY: string[] = [];

interface RunsColumnEditorProps {
  config: RunFeed.Config;
  fixedColumns?: string[];
  isTableExpandable?: boolean;
  onAgentChatOpened(): void;
  update(config: RunFeed.Config): void;
}

export const RunsTableEditor = (props: RunsColumnEditorProps) => {
  const {entityName, projectName} = useRunSelectorProjectInfo();
  const [search, searchDebounced, setSearch] = useDebounceState('', 200);

  const [searchMode, setMatchMode] = useState<MatchMode>('fuzzy');
  const pattern = useMemo(() => {
    if (searchDebounced === '' || searchMode === 'regex') {
      return null;
    }

    if (searchMode === 'exact') {
      return '%' + searchDebounced + '%';
    }

    return '%' + searchDebounced.split('').join('%') + '%';
  }, [searchDebounced, searchMode]);

  // strictly limit the query count when a user is searching so that the load isn't too much
  const queryCount = useMemo(() => (pattern == null ? 10000 : 200), [pattern]);

  const projectFieldsData = useProjectFieldsQuery({
    entityName,
    projectName,
    types: ['string', 'number', 'boolean', 'other'],
    columns: [
      'config',
      'summary_metrics',
      'aggregations_min',
      'aggregations_max',
    ],
    count: queryCount,
    pattern,
  });

  // These keys have broken behavior when they're not filtered out.
  // This filtering seems to be duplicated in other places.
  // Someone smarter than me should refactor the way we handle special keys.
  // Also filter out aggregation keys that are not visible
  const keys = projectFieldsData.keys.filter(key => {
    if (
      !Run.filterAggregationKeys(props.config.aggregationVisible || {}, key)
    ) {
      return false;
    }
    return !(
      key === 'config:wandb_version' ||
      key === 'summary:_runtime' ||
      key === 'summary:_step' ||
      key === 'summary:_timestamp'
    );
  });

  const defaultKeys = useDefaultKeys();

  const allColumns = useMemo(
    () => [...defaultKeys.map(k => Run.keyToString(k)), ...keys],
    [keys, defaultKeys]
  );

  return (
    <TableEditor
      allColumns={allColumns}
      config={props.config}
      fixedColumns={props.fixedColumns || EMPTY_ARRAY}
      onSetMatchMode={setMatchMode}
      onSetSearchQuery={setSearch}
      onUpdate={props.update}
      searchQuery={search}
      searchMode={searchMode}
      isTableExpandable={props.isTableExpandable}
      onAgentChatOpened={props.onAgentChatOpened}
    />
  );
};
